import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { useApiFetch } from "../api/apiClient";
import { colors } from "../utils/styles";
import { fmtDateTime } from "../utils/dateFormat";

const ROLES = ["SIGNER", "APPROVER"];

const emptyForm = {
  slot: "",
  recipientName: "",
  recipientEmail: "",
  role: "APPROVER",
  authType: "",
  title: "",
  isSubmissionSigner: false,
  isActive: true
};

export default function AdminAgreementRecipients() {
  const apiFetch = useApiFetch();
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null); // null | { mode: "create" } | { mode: "edit", id }
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await apiFetch("/admin/agreement-recipients");
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setRows(await res.json());
    } catch (err) {
      setError(err.message || "Failed to load agreement recipients.");
    }
  }, [apiFetch]);

  useEffect(() => {
    load();
  }, [load]);

  const openEdit = (row) => {
    setForm({
      slot: String(row.slot ?? ""),
      recipientName: row.recipientName || "",
      recipientEmail: row.recipientEmail || "",
      role: row.role || "APPROVER",
      authType: row.authType || "",
      title: row.title || "",
      isSubmissionSigner: !!row.isSubmissionSigner,
      isActive: !!row.isActive
    });
    setFormError(null);
    setEditing({ mode: "edit", id: row.id });
  };

  const closeDialog = () => {
    if (saving) return;
    setEditing(null);
  };

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleSave = async () => {
    const slotNum = parseInt(form.slot, 10);
    if (!Number.isInteger(slotNum) || slotNum < 1) {
      setFormError("Slot must be a positive whole number.");
      return;
    }
    if (!form.role) {
      setFormError("Role is required.");
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      const isEdit = editing.mode === "edit";
      const url = isEdit ? `/admin/agreement-recipients/${editing.id}` : "/admin/agreement-recipients";
      const method = isEdit ? "PUT" : "POST";
      const res = await apiFetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          slot: slotNum,
          recipientName: form.recipientName.trim() || null,
          recipientEmail: form.recipientEmail.trim() || null,
          role: form.role,
          authType: form.authType.trim() || null,
          title: form.title.trim() || null,
          isSubmissionSigner: form.isSubmissionSigner,
          isActive: form.isActive
        })
      });
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(text || `HTTP ${res.status}`);
      }
      setEditing(null);
      await load();
    } catch (err) {
      setFormError(err.message || "Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ width: "100%", mt: 2 }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
        Agreement Workflow & Routing for Adobe Sign
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Routing recipients for the City/District Agreement Authorized Signers reports. These reports are used
        as the csv upload files when sending out the agreements through Adobe Sign.
      </Typography>

      {error && (
        <Box
          sx={{ p: 2, mb: 2, bgcolor: colors.errorBg, color: colors.error, borderRadius: 1, fontSize: 13 }}
        >
          {error}
        </Box>
      )}

      {!error && rows === null && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {rows && rows.length === 0 && (
        <Box sx={{ p: 3, textAlign: "center", color: "text.secondary" }}>No recipients yet.</Box>
      )}

      {rows && rows.length > 0 && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead
              sx={{ "& .MuiTableCell-head": { bgcolor: colors.darkGray, color: "white", fontWeight: 700 } }}
            >
              <TableRow>
                <TableCell align="right">Routing Order</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Auth Type</TableCell>
                <TableCell>Title</TableCell>
                <TableCell align="center">Submission Signer</TableCell>
                <TableCell align="center">Active</TableCell>
                <TableCell>Last updated</TableCell>
                <TableCell align="right" sx={{ width: 100 }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell align="right">{r.slot}</TableCell>
                  <TableCell>
                    {r.recipientName || (r.isSubmissionSigner ? "(from submission)" : "—")}
                  </TableCell>
                  <TableCell>
                    {r.recipientEmail || (r.isSubmissionSigner ? "(from submission)" : "—")}
                  </TableCell>
                  <TableCell>{r.role}</TableCell>
                  <TableCell>{r.authType || "—"}</TableCell>
                  <TableCell>{r.title || "—"}</TableCell>
                  <TableCell align="center">{r.isSubmissionSigner ? "Yes" : "—"}</TableCell>
                  <TableCell align="center">{r.isActive ? "Yes" : "No"}</TableCell>
                  <TableCell>
                    {r.updatedAt ? (
                      <Tooltip
                        arrow
                        title={
                          r.createdAt
                            ? `Created by ${r.createdByName || r.createdBy || "—"} on ${fmtDateTime(r.createdAt)}`
                            : "No creation record"
                        }
                      >
                        <span>
                          {fmtDateTime(r.updatedAt)}
                          <Typography variant="caption" display="block" color="text.secondary">
                            {r.updatedByName || r.updatedBy || "—"}
                          </Typography>
                        </span>
                      </Tooltip>
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {r.slot !== 1 && (
                      <IconButton size="small" onClick={() => openEdit(r)} aria-label="Edit">
                        <EditIcon fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={editing !== null} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle>Edit Recipient</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Routing Order"
              select
              value={form.slot}
              onChange={(e) => setField("slot", e.target.value)}
              required
              fullWidth
              autoFocus
              helperText="Routing position (2–5; 1 is the submission signer and isn't editable)."
            >
              {["2", "3", "4", "5"].map((n) => (
                <MenuItem key={n} value={n}>
                  {n}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Name"
              value={form.recipientName}
              onChange={(e) => setField("recipientName", e.target.value)}
              fullWidth
              disabled={form.isSubmissionSigner}
              helperText={form.isSubmissionSigner ? "Filled from the submission." : ""}
            />
            <TextField
              label="Email"
              type="email"
              value={form.recipientEmail}
              onChange={(e) => setField("recipientEmail", e.target.value)}
              fullWidth
              disabled={form.isSubmissionSigner}
            />
            <TextField
              label="Role"
              select
              value={form.role}
              onChange={(e) => setField("role", e.target.value)}
              required
              fullWidth
            >
              {ROLES.map((r) => (
                <MenuItem key={r} value={r}>
                  {r}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Auth Type"
              select
              value={form.authType}
              onChange={(e) => setField("authType", e.target.value)}
              fullWidth
            >
              <MenuItem value="">None</MenuItem>
              <MenuItem value="EMAIL_OTP">EMAIL_OTP</MenuItem>
            </TextField>
            {form.slot === "5" && (
              <TextField
                label="Title"
                value={form.title}
                onChange={(e) => setField("title", e.target.value)}
                fullWidth
                helperText="This title is printed on the agreement for the ATC final signer. "
              />
            )}
            <FormControlLabel
              control={
                <Checkbox checked={form.isActive} onChange={(e) => setField("isActive", e.target.checked)} />
              }
              label="Active (included in the reports)"
            />
            {formError && (
              <Box
                sx={{ p: 1.5, bgcolor: colors.errorBg, color: colors.error, borderRadius: 1, fontSize: 13 }}
              >
                {formError}
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
